import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { APP_EVENTS, appEventEmitter } from '../utils/eventEmitter.js';
import * as utils from '../utils/utils.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check if user is logged in when App loads
        setIsLoading(true);
        userInfo().then((response) => {
            if (response && response.status === 200) {
                setCurrentUser(response.data.user);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    }, []);

    // Listen for session expiration events from API client
    useEffect(() => {
        const handleSessionExpired = (eventData) => {
            console.warn('🔒 Session expired event received - logging out user');
            console.warn('🔒 Event data:', eventData);

            // Clear all auth data from localStorage
            localStorage.removeItem('name');
            localStorage.removeItem('email');
            localStorage.removeItem('user_id');

            // Clear current user state
            setCurrentUser(null);

            // Show user-friendly notification
            toast.error('Your session has expired. Please log in again.', {
                autoClose: 3000,
            });

            // Redirect to login page
            // Using window.location instead of navigate because AuthProvider
            // is rendered before Router context is available
            setTimeout(() => {
                window.location.href = '/login';
            }, 500); // Small delay to allow toast to be visible
        };

        // Subscribe to session expiration events
        appEventEmitter.on(APP_EVENTS.SESSION_EXPIRED, handleSessionExpired);

        // Cleanup listener on unmount
        return () => {
            appEventEmitter.off(APP_EVENTS.SESSION_EXPIRED, handleSessionExpired);
        };
    }, []);

    const signIn = async (email, password) => {
        const server_url = utils.backendUrl() + `/login`;

        try {
            const response = await axios.post(
                server_url,
                { email, password },
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            console.log('Login response user:', response.data.user);

            localStorage.setItem('name', response.data.user.name);
            localStorage.setItem('email', response.data.user.email);
            localStorage.setItem('user_id', response.data.user.user_id);

            setCurrentUser(response.data.user);

            return response;
        } catch (err) {
            console.log(err.message);
            if (err.response && err.response.status === 401) {
                return err.response;
            }
            console.log('login error', err.message);
            console.log(err.response);
        }
    };

    const signOut = async () => {
        const server_url = utils.backendUrl() + `/logout`;
        try {
            const response = await axios.post(
                server_url,
                {},
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            localStorage.removeItem('name');
            localStorage.removeItem('email');
            localStorage.removeItem('user_id');
            setCurrentUser(null);

            return response;
        } catch (err) {
            console.log(err.message);
            if (err.response && err.response.status === 401) {
                return err.response;
            }
            console.log('logout error', err.message);
            console.log(err.response);
        }
    };
    const updateAgeGroupsPreference = async (show_age_groups) => {
        const server_url = utils.backendUrl() + `/update-age-groups-preference`;

        try {
            const response = await axios.patch(
                server_url,
                { show_age_groups },
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response && response.status === 200) {
                // Update current user with new preference
                setCurrentUser(response.data.user);
            }

            return response;
        } catch (err) {
            console.log('Error updating age groups preference:', err.message);
            if (err.response && err.response.status === 401) {
                return err.response;
            }
            console.log(err.response);
        }
    };

    const value = {
        currentUser,
        isLoading,
        signIn,
        signOut,
        updateAgeGroupsPreference,
    };

    const userInfo = async () => {
        const server_url = utils.backendUrl() + `/user-info`;

        try {
            const response = await axios.post(
                server_url,
                {
                    email: localStorage.getItem('email'),
                },
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response;
        } catch (err) {
            console.log(err.message);
            if (err.response && err.response.status === 401) {
                return err.response;
            }
            console.log(err.response);
        }
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
